import { Component, Inject, ViewChild, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';

import { DialogLocationLabel } from '../../../interfaces';
import { DialogLocationLabels, DialogLocationTags } from './dialog-location.labels';
import { GenericRegexp } from './../../../regexp/generic.regexp';
import { LocationProvider } from '../../../providers/locations/location-provider.service';
import { Locations } from '../../../interfaces/locations';
import { PaginatorService } from '../../../services/paginator/paginator.service';
import { ToastrAlertsService } from '../../../services/utils/toastr-alerts.service';

const DISPLAYEDCOLUMNS = ['identifier', 'name', 'address', 'postalCode', 'state'];
const IDENTIFIER_KEY = 'identifier';
const ORIGIN = 'Origen';
const SEARCH_BY_IDENTIFIER = 2;
const SEARCH_PARAM_BY_NAME = 'nombre';
const SEARCH_PARAM_BY_ZIP = 'codigoPostal';

@Component({
  selector: 'app-dialog-location',
  templateUrl: './dialog-location.component.html',
  styleUrls: ['./dialog-location.component.scss', '../../../app.component.scss']
})

export class DialogLocationComponent implements OnInit {
  public dataSource: MatTableDataSource<Locations>;
  public displayedColumns: Array<string>;
  public identifierFormGroup: FormGroup;
  public labels: DialogLocationLabel;
  public locationInfo: Object;
  public locationsAux: Array<any>;
  public locationsAuxLength: any;
  public locationType: string;
  public nameFormGroup: FormGroup;
  public orderType: string;
  public postalCodeFormGroup: FormGroup;
  public searchValue: any;
  public searchValueFormControl: any;
  public selected: any;
  public visible: boolean;

  constructor(
    private locationsProvider: LocationProvider,
    public dialogRef: MatDialogRef<DialogLocationComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly builder: FormBuilder,
    private providerLabels: PaginatorService,
    private toast: ToastrAlertsService
  ) { this.labels = DialogLocationTags; }

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  ngOnInit() {
    this.locationType = this.data.title;
    this.locationInfo = this.data.location;
    this.orderType = this.data.orderType ? this.data.orderType : null;
    this.visible = false;
    this.locationsAux = [];
    this.selected = new FormControl(0);
    this.searchValueFormControl = new FormControl('', Validators.required);
    this.displayedColumns = DISPLAYEDCOLUMNS;
    this.initForm(this.builder);
    this.providerLabels.paginatorLabels(this.paginator);
  }

  public async getLocations(searchOptionSelected: any) {
    this.dataSource = new MatTableDataSource([]);
    if (this.locationType === ORIGIN) {
      let searchOriginLocationsBy = null;
      if (searchOptionSelected === 0) {
        this.searchValue = this.postalCodeFormGroup.value.postalCodeValue;
        searchOriginLocationsBy = SEARCH_PARAM_BY_ZIP;
      } else if (searchOptionSelected === SEARCH_BY_IDENTIFIER) {
        this.searchValue = this.identifierFormGroup.value.identifier.trim();
        searchOriginLocationsBy = IDENTIFIER_KEY;
      } else {
        this.searchValue = this.nameFormGroup.value.nameValue.trim();
        searchOriginLocationsBy = SEARCH_PARAM_BY_NAME;
      }
      try {
        const res = await this.locationsProvider.getOrigin(searchOptionSelected, this.searchValue.trim(), searchOriginLocationsBy);
        this.groupLocations(res);
        if (this.locationsAux.length === 0) {
          this.visible = false;
          this.toast.warningAlert(DialogLocationLabels.noLocationsFound);
        } else {
          this.dataSource.paginator = this.paginator;
          this.visible = true;
        }
      } catch (error) {
        console.log(error);
        this.toast.warningAlert(DialogLocationLabels.noLocationsFoundByShipper);
      }
    } else {
      let searchDestinationsBy = null;
      if (searchOptionSelected === 0) {
        this.searchValue = this.postalCodeFormGroup.value.postalCodeValue;
        searchDestinationsBy = SEARCH_PARAM_BY_ZIP;
      } else if (searchOptionSelected === SEARCH_BY_IDENTIFIER) {
        this.searchValue = this.identifierFormGroup.controls[IDENTIFIER_KEY].value.trim();
        searchDestinationsBy = IDENTIFIER_KEY;
      } else {
        this.searchValue = this.nameFormGroup.value.nameValue;
        searchDestinationsBy = SEARCH_PARAM_BY_NAME;
      }

      try {
        const res = await this.locationsProvider.getDestination(searchOptionSelected, this.searchValue.trim(), searchDestinationsBy);
        this.groupLocations(res);
        if (this.locationsAux.length === 0) {
          this.visible = false;
          this.toast.warningAlert(DialogLocationLabels.noLocationsFound);
        } else {
          this.dataSource.paginator = this.paginator;
          this.visible = true;
        }
      } catch (error) {
        console.log(error);
        this.toast.warningAlert(DialogLocationLabels.noLocationsFoundByShipper);
      }
    }
  }

  private groupLocations(res: object): void {
    const locations: any = res;
    this.locationsAux = [];
    locations.ubicaciones.forEach(location => {
      const details = {
        _id: location._id,
        identifier: location.clienteId,
        locationId: location.clienteId,
        locationRFC: location.rfc,
        name: location.nombre,
        address: location.direccion,
        postalCode: location.codigoPostal,
        municipality: location.municipio,
        state: location.estado,
        settlement: location.colonia,
        latitude: location.latitud,
        longitude: location.longitud,
        documentationDirections: location.documentationDirections ? location.documentationDirections : [],
        evidenceDirections: location.evidenceDirections ? location.evidenceDirections : []
      };
      this.locationsAux.push(details);
    });
    this.dataSource = new MatTableDataSource(this.locationsAux);
  }

  applyFilter(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  public getRecord(info: object): void {
    this.dialogRef.close(info);
  }

  public onClickClose(): void {
    if (this.locationInfo) {
      this.dialogRef.close(this.locationInfo);
    } else {
      this.dialogRef.close();
    }
  }

  private initForm(fb: FormBuilder): void {
    this.postalCodeFormGroup = fb.group({
      postalCodeValue: new FormControl(null, [Validators.required, Validators.minLength(1), Validators.maxLength(5)])
    });
    this.nameFormGroup = fb.group({
      nameValue: new FormControl(null, [Validators.required])
    });
    this.identifierFormGroup = fb.group({
      identifier: new FormControl(null, [Validators.required, Validators.pattern(GenericRegexp.NON_EMPTY_STRING)])
    });
  }

  public onKeyPressCode(event: KeyboardEvent): void {
    const pattern = /[0-9\b]/;
    const inputChar = event.key;

    if (!pattern.test(inputChar)) {
      event.preventDefault();
    }
  }

}
